import { useEffect, useState } from "react";

/**
 * Frontend safety-net for misconfigured Google OAuth redirect_uri.
 *
 * THE PROBLEM:
 *   Google's authorized redirect URI must point at the BACKEND route
 *   `/api/oauth/calendar/callback`. If the production env var
 *   `GOOGLE_CALENDAR_REDIRECT_URI` is mistakenly set without the `/api/`
 *   prefix (e.g. `https://staff.familychapel.org/oauth/calendar/callback`),
 *   Google redirects users to the FRONTEND. The React SPA catch-all would
 *   either 404 or eat the `?code=` query string by navigating to `/`, and
 *   the OAuth flow never completes.
 *
 * THE FIX:
 *   This component matches that exact misconfigured path on the frontend,
 *   then forwards the entire URL (including the `?code=...&scope=...` Google
 *   appended) to the backend route, where the real token-exchange logic
 *   lives. The user sees a brief "Completing Google sign-in…" screen and
 *   then lands on /settings — same UX as the well-configured path.
 *
 * This route is purely defensive — once the env var is fixed in the deploy
 * panel, Google will redirect directly to the backend and this component
 * never runs. It exists so a single misconfiguration never blocks reconnect.
 */
export default function OAuthCallbackBridge() {
  const [phase, setPhase] = useState("forwarding");

  useEffect(() => {
    // Preserve the full query string so the backend gets `code` + any
    // optional fields Google appends (state, scope, authuser, prompt).
    const qs = window.location.search || "";
    const target = `/api/oauth/calendar/callback${qs}`;
    // Use `replace` so this transient bridge URL never lands in browser
    // history — back-button from /settings shouldn't bring the user back
    // to a stale OAuth code that's already been consumed.
    try {
      window.location.replace(target);
    } catch (e) {
      setPhase("failed");
    }
    // If the redirect doesn't fire within 4s (e.g. service worker
    // interception or some network weirdness), fall through to a manual
    // continue button. Better UX than spinning forever.
    const t = setTimeout(() => setPhase("stuck"), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="oauth-callback-bridge"
      className="min-h-screen flex items-center justify-center bg-cream px-4"
    >
      <div className="max-w-md w-full bg-white rounded-2xl border border-brand-primary/10 shadow-warm p-8 text-center">
        <div className="h-12 w-12 mx-auto rounded-full bg-terracotta/10 grid place-items-center mb-5">
          <div className="h-5 w-5 rounded-full border-2 border-terracotta border-r-transparent animate-spin" />
        </div>
        <h1 className="font-heading text-xl text-brand-primary mb-2">
          Completing Google sign-in…
        </h1>
        <p className="text-sm text-brand-primary/65 leading-relaxed">
          You&apos;ll be redirected to Settings in a moment.
        </p>
        {phase === "stuck" && (
          <div className="mt-6 text-left">
            <p className="text-xs text-terracotta font-medium mb-2">
              Taking longer than usual.
            </p>
            <a
              href={`/api/oauth/calendar/callback${window.location.search || ""}`}
              data-testid="oauth-callback-bridge-manual"
              className="inline-flex items-center justify-center w-full rounded-xl bg-terracotta text-white px-4 py-2.5 text-sm font-medium hover:bg-terracotta-hover transition-colors"
            >
              Continue to Settings
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
